/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './TransientDataStore';
import { camelCase } from 'lodash-es';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { emptyFunction } from '@cloudio-saas/statex';

const TransientDataStoreManager: ComponentManagerFn<'TransientDataStore'> = (
  platform: Platform,
) => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'TransientDataStore'>(
      platform,
      'TransientDataStore',
    ),
    type: 'TransientDataStore',
    name: 'Transient Data Store',
    defaultValue,
    replaceableWith: ['ReadOnlyDataStore', 'DataStore', 'LocalDataStore'],
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'hideDirtyIndicator':
        case 'controlledBy':
          return false;
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        default:
          return !!item.alias;
      }
    },
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    onPropertyChange: (property, value, itemData, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
          alias: '',
        };
      }
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    category: 'Data',
    icon: { iconName: 'database' },
    canHaveChildren: () => true,
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    description: `**Overview**<br/>
The Transient Data Store is a specialized data container that mimics the behavior of a standard Data Store—supporting Insert, Update, and Delete operations—but never saves changes to the database.
It acts as a "Sandbox" or "Draft" layer. You can bind Forms and Grids to it, perform complex validations, and manipulate records in the UI, knowing that the backend data remains untouched. This is ideal for "What-If" scenarios, complex wizards, or temporary workspaces.

**1. Core Concept: The "Sandbox"**<br/>
Unlike a Local Data Store (which has no schema) or a ReadOnly Data Store (which forbids editing), the Transient Data Store sits in the middle:
* **Schema-Aware:** It connects to a real Data Source to understand your data structure (fields, types, validation rules).
* **Fully Editable:** The UI behaves exactly like a production page. You can add rows, edit cells, and delete records.
* **Memory-Only Persistence:** When you trigger a Save() action, the store validates the data and updates its internal state, but it suppresses the database commit. The data vanishes when the session ends or the page reloads.

**2. Configuration**<br/>
* **Data Source:** Select the backend entity (e.g., "SalesOrder") to define the structure.
* **Alias:** The unique handle used to bind UI components (e.g., "draftOrder").
* **Singleton:**
    * **Enabled:** The transient state is shared across multiple pages. This is perfect for Multi-Step Wizards where a user builds an object across Screen A, Screen B, and Screen C, and only commits it to the real database on the final step.

**3. Strategic Use Cases**<br/>
* **Draft Mode:** Allow users to create a complex order with multiple line items. If they cancel, no "orphaned" records are left in your database. If they confirm, you can copy the transient data to a real Data Store and save it.
* **Simulations:** Build a "Mortgage Calculator" or "Pricing Estimator" where users manipulate inputs to see results without affecting actual records.
* **Cloning/Templating:** Load a real record into a Transient Store, let the user tweak it, and then save it as a new entry.

**4. Advanced Settings**<br/>
* **Ignore Primary Key Duplicates:** Since transient data often involves cloning or temporary mocking, you might generate temporary IDs that collide. Enabling this prevents the store from throwing errors on duplicate keys during the draft phase.

**5. Transitioning**<br/>
This component is fully compatible with the platform's replacement logic. You can build your entire UI using a Transient Data Store for safe testing, and then use the **Replace With** feature to swap it for a Data Store (Production) or Local Data Store (Prototype) without breaking your bindings.`,
  };
};

export default TransientDataStoreManager;
